import { UIElement } from '../ui-element.js';
import { createElement } from '../../global/render-api.js';
import { Labels } from '../../global/labels.js';
import { UIIcon } from '../icon/ui-icon.js';
import { createPictureWithSources } from '../../global/ui-helpers.js';
// TODO GUI-3283 cleanup
import stylesOfferLegacy from './ui-offer.css';
import stylesOfferIntro from './ui-offer-intro-v2.css';
import stylesOfferLogout from './ui-offer-logout.css';
// import stylesOfferPersonal from './ui-offer-personal-v2.css';
import stylesPega from './ui-offer-pega.css';
import { browser } from '../../global/index.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UIOffer
 * @element ui-offer
 * @classdesc Represents a class for <code>ui-offer</code> element.
 * Element for marketing offer in general. On close component fires event
 * 'offer-close'.
 * @fires event:offer-close
 * @property {("personal" | "introduction" | "logout")} [layout="introduction"] {@attr layout} Layout of offer.
 * @property {string} [image] {@attr image} Proxy image attribute for &lt;figure> tag.
 * @property {string} [imageMobile] {@attr image-mobile} Proxy image attribute for &lt;figure> tag.
 * @property {string} [alt] {@attr alt} Alternate text for image same as 'alt=""'.
 * @property {boolean} [highlighted] {@attr highlighted} Adds background (white) to ui-offer.
 * @property {string} [category] {@attr category} Category of personal offer.
 * Only for layout="personal".
 * @property {string} [thumbText] {@attr thumb-text} Text for ui-slides thumb.
 * Works only inside ui-slides.
 * @property {boolean} [closable] {@attr closable} add close functionality for offers.
 * Only layout="personal".
 * @property {boolean} [imageNocrop] {@attr image-nocrop} image-no-crop (legacy).
 * @property {"bottom" | "none"} [mobileImage] {@attr mobile-image} Position of the image on mobile
 * @property {"top" | "bottom"} [imagePosition] {@attr image-position} Position of the image
 *  {@desc bottom}
 *  {@desc top}
 * @property {string} [offerId] {@attr offer-id} Offer's ID for closable functionality
 * it will be sent to back system of ibank or any other.
 * @property {string} [offerStatus] {@attr offer-status} Offer's status for closable
 * functionality it will be sent to back system of ibank or any other system.
 * @property {UIButtonbar} buttonbar {@readonly} Shortcut to button bar inside offer if exists.
 * @property {HTMLButtonElement|null} closeBtn {@readonly} Shortcut to close button if offer have
 * 'closable' attribute.
 * @property {HTMLElement[]|null} disclaimers {@readonly} Shortcut to all elements with
 * .disclaimer CSS classes.
 * @property {HTMLElement|null} article {@readonly} Shortcut to article element.
 * @property {HTMLElement|null} figure {@readonly} Shortcut to figure element.
 * @slot
 * @example
 * <ui-offer layout="personal">
 *   <figure>
 *     <img src="assets/mocks/images/offer06.jpg" alt="Discount">
 *   </figure>
 *   <article>
 *     <h2>Only today is discount 20%</h2>
 *     <p>
 *       Travel insurance only today 20 percent cheaper.
 *     </p>
 *     <ui-buttonbar>
 *       <button class="button">Apply</a>
 *     </ui-buttonbar>
 *   </article>
 * </ui-offer>
 * @example <caption>or shorten version</caption>
 * <ui-offer layout="personal" image="/proinfo-files/discount.jpg">
 *   <h2>Only today is discount 20%</h2>
 *   <p>
 *     Travel insurance only today 20 percent cheaper.
 *   </p>
 *   <ui-buttonbar>
 *     <button class="button">Apply</a>
 *   </ui-buttonbar>
 * </ui-offer>
 */
class UIOffer extends UIElement {
    /**
     * @type {Props}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                image: String,
                alt: {
                    type: String,
                    default: '',
                },
                category: String,
                layout: { type: String, default: this.Layout.Introduction },
                imageNocrop: Boolean,
                imageMobile: String,
                closable: Boolean,
                offerId: String,
                offerStatus: String,
            },
            children: {
                buttonbar: 'ui-buttonbar',
                closeBtn: '.ui-offer__close-button',
                figure: ':scope > figure',
                article: ':scope > article',
                disclaimers: {
                    selector: '.disclaimer',
                    multiple: true,
                },
            },
        };
    }

    /**
     * @type {UIShapeLayouts}
     */
    static get Layout() {
        return {
            Introduction: 'introduction',
            Introduction2: 'introduction', // TODO GUI-3283 cleanup
            Personal: 'personal',
            Personal2: 'personal.v2', // TODO GUI-3283 cleanup
            Pega: 'pega',
            PegaTip: 'pega-tip',
            Logout: 'logout',
        };
    }

    /**
     * @type {Labels}
     * @readonly
     */
    static get labels() {
        return Labels.attach('ui-offer', {
            dismissOffer: 'Dismiss offer',
        });
    }

    /**
     * @type {string}
     */
    static get CloseEventName() {
        return 'offer-close';
    }

    /**
     * @type {string}
     */
    static get FigureElementsSelector() {
        return ['ui-shape', ':scope > img', 'picture', 'ui-video'].join(',');
    }

    /**
     * Creates HTML for category section.
     * @private
     * @returns {HTMLDivElement}
     */
    createCategoryElem() {
        return createElement({
            tagName: 'div',
            classList: {
                'ui-offer__category': true,
            },
            children: [this.category],
        });
    }

    /**
     * Creates layout from given attributes.
     * @private
     */
    createOffer() {
        this.positionButtons();
        const sources = [];
        let shouldHavePicture = false;

        if (this.isRedesignLayout()) {
            shouldHavePicture = true;
            if (this.imageMobile) {
                sources.push({
                    media: browser.constructor.MediaBreakpoint.Mobile,
                    srcset: this.imageMobile,
                });
            }
        }

        let article = this.querySelector('article');
        const badge = this.querySelector('ui-badge');
        const remark = this.querySelector('ui-remark');
        if (!article) {
            article = this.createElement({
                tagName: 'article',
                children: [].filter.call(
                    this.childNodes,
                    (node) => !['figure', 'img'].includes(node.localName)
                ),
            });
            this.insertAdjacentElement('beforeend', article);
        }

        if (this.image) {
            this.insertBefore(
                this.createElement({
                    tagName: 'figure',
                    children: [
                        createPictureWithSources(
                            sources,
                            this.image,
                            this.alt,
                            shouldHavePicture
                        ),
                        remark,
                        badge,
                    ],
                }),
                article
            );
        }

        // If user does not wrap them in <figure> tag
        const figureElems = [
            ...this.querySelectorAll(this.constructor.FigureElementsSelector),
        ];
        const figure = this.figure || document.createElement('figure');
        figureElems.forEach((element) => {
            if (
                !['figure', 'picture'].includes(element.parentElement.localName)
            ) {
                badge && figureElems.push(badge);
                remark && figureElems.push(remark);
                figure.append(...figureElems);
            }
        });
        if (figure.children.length > 0) {
            this.insertBefore(figure, article);
        }

        if (this.closable) {
            const actionsContainer = this.createElement({
                tagName: 'div',
                classList: {
                    'ui-offer__actions': true,
                },
                children: [
                    {
                        tagName: 'button',
                        attributes: {
                            type: 'button',
                            title: this.constructor.labels['dismissOffer'],
                        },
                        classList: {
                            'ui-offer__close-button': true,
                            '-iconed': true,
                        },
                        children: [
                            {
                                tagName: 'ui-icon',
                                attributes: {
                                    glyph:
                                        this.layout ===
                                        this.constructor.Layout.Personal2
                                            ? 'redesign-x-mark'
                                            : 'cross',
                                    color: UIIcon.colors.DEFAULT,
                                },
                            },
                        ],
                    },
                ],
            });
            this.prepend(actionsContainer);
        }

        if (this.category) {
            article.insertAdjacentElement(
                'afterbegin',
                this.createCategoryElem()
            );
        }

        const shouldDisplaceDisclaimers =
            this.disclaimers.length > 0 &&
            this.layout === this.constructor.Layout.Introduction2;
        if (shouldDisplaceDisclaimers) {
            [].forEach.call(this.disclaimers, (disclaimer) => {
                article.insertAdjacentElement('afterend', disclaimer);
            });
        }

        this.classList.toggle(
            '-no-figure',
            this.figure?.parentElement !== this
        );

        // :has()
        if (this.querySelector('ui-shape')) {
            this.classList.add('-has-shape');
        }

        // Add inner div wrapper around all children if needed
        if (this.isPegaOffer() || this.isPegaTip()) {
            const innerWrapper = this.createElement({
                tagName: 'div',
                classList: { 'ui-offer__inner-wrapper': true },
            });
            // Move all existing children into the wrapper
            innerWrapper.append(...this.children);
            this.appendChild(innerWrapper);
        }
    }

    /**
     * @returns {HTMLPictureElement | HTMLImageElement}
     * @private
     */
    createPicture() {
        if (this.isRedesignLayout()) {
            return createElement({
                tagName: 'picture',
                children: [
                    this.imageMobile && {
                        tagName: 'source',
                        attributes: {
                            media: browser.constructor.MediaBreakpoint.Mobile,
                            srcset: this.imageMobile,
                        },
                    },
                    {
                        tagName: 'img',
                        attributes: {
                            src: this.image,
                            alt: this.alt || '',
                        },
                    },
                ],
            });
        } else {
            return createElement({
                tagName: 'img',
                attributes: {
                    src: this.image,
                    alt: this.alt || '',
                },
            });
        }
    }

    /**
     * @private
     * @returns {boolean}
     */
    isRedesignLayout() {
        return [
            this.constructor.Layout.Introduction2,
            this.constructor.Layout.Personal2,
            this.constructor.Layout.Pega,
            this.constructor.Layout.PegaTip,
        ].includes(this.layout);
    }

    /**
     * @private
     * @returns {boolean}
     */
    canBeClosed() {
        return !!(this.closable && this.closeBtn);
    }

    /**
     * @private
     * @returns {boolean}
     */
    isIntroductionOffer() {
        return [
            this.constructor.Layout.Introduction,
            this.constructor.Layout.Introduction2,
        ].includes(this.layout);
    }

    /**
     * @private
     * @returns {boolean}
     */
    isPersonalOffer() {
        return [
            this.constructor.Layout.Personal,
            this.constructor.Layout.Personal2,
        ].includes(this.layout);
    }

    /**
     * @private
     * @returns {boolean}
     */
    isPegaOffer() {
        return this.layout === this.constructor.Layout.Pega;
    }

    /**
     * @private
     * @returns {boolean}
     */
    isPegaTip() {
        return this.layout === this.constructor.Layout.PegaTip;
    }

    /**
     * Position buttons inside button-bar correctly.
     * @private
     */
    positionButtons() {
        if (!this.buttonbar) {
            return;
        }
        const buttonsAndLinks = this.buttonbar.querySelectorAll('button, a');
        if (
            this.isIntroductionOffer() ||
            this.layout === this.constructor.Layout.Personal2 ||
            this.isPegaOffer() ||
            this.isPegaTip()
        ) {
            [].forEach.call(buttonsAndLinks, (item) =>
                item.classList.add('-left')
            );
        }
    }

    /**
     * When offer closed.
     * @private
     */
    onClose() {
        this.dispatchCustomEvent(this.constructor.CloseEventName, {
            id: this.offerId,
            status: this.offerStatus,
        });
    }

    /**
     * @inheritDoc
     */
    render() {
        this.createOffer();
    }

    /**
     * @inheritDoc
     */
    hydrate() {
        if (this.canBeClosed()) {
            this.onCloseHandler = this.onClose.bind(this);
            this.closeBtn.addEventListener('click', this.onCloseHandler);
        }
    }

    /**
     * @inheritDoc
     */
    disconnect() {
        if (this.canBeClosed()) {
            this.closeBtn.removeEventListener('click', this.onCloseHandler);
        }
    }

    /**
     * @inheritDoc
     */
    reconnect() {
        if (this.canBeClosed()) {
            this.onCloseHandler = this.onClose.bind(this);
            this.closeBtn.addEventListener('click', this.onCloseHandler);
        }
    }
}

UIOffer.defineElement('ui-offer', [
    ...stylesOfferLegacy,
    ...stylesOfferIntro,
    ...stylesPega,
    ...stylesOfferLogout,
]);
export { UIOffer };
